import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/lib/api'

export const AUTO_REPORT_STORAGE_PREFIX = 'roostr:auto_report_unavailable:'
export const AUTO_REPORT_COOLDOWN_MS = 24 * 60 * 60 * 1000

function getBooleanEnv(name: string, defaultValue: boolean): boolean {
  const value = process.env[name]
  if (value === undefined) return defaultValue
  return value === 'true'
}

const DEAD_DETECTION_ENABLED = getBooleanEnv(
  'NEXT_PUBLIC_DEAD_LISTING_DETECTION_ENABLED',
  false,
)
const DEAD_DETECTION_AUTO_REPORT_ENABLED = getBooleanEnv(
  'NEXT_PUBLIC_DEAD_LISTING_DETECTION_AUTO_REPORT_ENABLED',
  false,
)

function meetsThreshold(total: number, broken: number): boolean {
  return (
    (total >= 3 && broken >= 3) ||
    (total > 0 && total <= 2 && broken >= total)
  )
}

function isOnCooldown(listingId: string): boolean {
  try {
    const key = `${AUTO_REPORT_STORAGE_PREFIX}${listingId}`
    const last = Number(localStorage.getItem(key) || '0')
    return !!last && Date.now() - last < AUTO_REPORT_COOLDOWN_MS
  } catch {
    return false
  }
}

function markReported(listingId: string): void {
  try {
    const key = `${AUTO_REPORT_STORAGE_PREFIX}${listingId}`
    localStorage.setItem(key, String(Date.now()))
  } catch {
    // localStorage blocked — silently skip
  }
}

interface UseDeadListingDetectionOpts {
  listingId: string
  imageUrls: string[]
  mode: 'probe' | 'observe'
  enabled?: boolean
}

export function useDeadListingDetection({
  listingId,
  imageUrls,
  mode,
  enabled = true,
}: UseDeadListingDetectionOpts): {
  isDead: boolean
  reportBrokenImage: (index: number) => void
} {
  const [isDead, setIsDead] = useState(false)
  const brokenRef = useRef<Set<number>>(new Set())
  const reportedRef = useRef(false)
  const detectionEnabled = DEAD_DETECTION_ENABLED && enabled

  const doReport = useCallback(() => {
    if (reportedRef.current) return
    reportedRef.current = true
    markReported(listingId)
    setIsDead(true)
    if (DEAD_DETECTION_AUTO_REPORT_ENABLED) {
      void api.reportUnavailable(listingId).catch(() => {})
    }
  }, [listingId])

  // observe mode: caller pushes broken indices
  const reportBrokenImage = useCallback(
    (index: number) => {
      if (!detectionEnabled) return
      if (mode !== 'observe') return
      if (reportedRef.current) return
      brokenRef.current.add(index)
      if (meetsThreshold(imageUrls.length, brokenRef.current.size)) {
        if (!isOnCooldown(listingId)) {
          doReport()
        } else {
          setIsDead(true)
        }
      }
    },
    [detectionEnabled, mode, imageUrls.length, listingId, doReport],
  )

  // probe mode: when enabled flips true, silently load up to 3 extra images
  // A random stagger (0-3s) prevents many cards from probing the CDN at once.
  useEffect(() => {
    if (!detectionEnabled) return
    if (mode !== 'probe') return
    if (reportedRef.current) return

    // Cooldown check before any probing — zero wasted requests
    if (isOnCooldown(listingId)) {
      setIsDead(true)
      return
    }

    const total = imageUrls.length
    if (total === 0) return

    // Index 0 is already broken (that's what triggered enabled)
    brokenRef.current.add(0)

    if (meetsThreshold(total, brokenRef.current.size)) {
      doReport()
      return
    }

    // Probe up to 3 additional images (indices 1..3)
    const probeIndices = imageUrls
      .slice(1, 4)
      .map((_, i) => i + 1)

    const images: HTMLImageElement[] = []
    let cancelled = false

    const staggerMs = Math.random() * 3000
    const timer = setTimeout(() => {
      if (cancelled) return

      for (const idx of probeIndices) {
        const img = new window.Image()
        images.push(img)

        img.onerror = () => {
          if (reportedRef.current) return
          brokenRef.current.add(idx)
          if (meetsThreshold(total, brokenRef.current.size)) {
            doReport()
          }
        }
        img.src = imageUrls[idx]
      }
    }, staggerMs)

    return () => {
      cancelled = true
      clearTimeout(timer)
      for (const img of images) {
        img.onload = null
        img.onerror = null
        img.src = ''
      }
    }
  }, [detectionEnabled, mode, listingId, imageUrls, doReport])

  return { isDead, reportBrokenImage }
}
